import { ChevronDown } from 'lucide-react';

import { Checkbox } from 'components/ui/checkbox';
import { Popover, PopoverContent, PopoverTrigger } from 'components/ui/popover';

import { areAllTicketsSelected, type TicketOption } from './ticketFilter';

type TicketFilterDropdownProps = {
    ticketOptions: TicketOption[];
    selectedTicketIds: number[] | undefined;
    onChange: (ticketIds: number[] | undefined) => void;
    disabled?: boolean;
};

function getTriggerLabel(
    ticketOptions: TicketOption[],
    selectedTicketIds: number[] | undefined,
    isAll: boolean,
): string {
    if (isAll) {
        return 'All Tickets';
    }

    if (!selectedTicketIds || selectedTicketIds.length === 0) {
        return 'No tickets';
    }

    if (selectedTicketIds.length === 1) {
        return (
            ticketOptions.find((option) => option.id === selectedTicketIds[0])?.name ?? '1 ticket'
        );
    }

    return `${selectedTicketIds.length} tickets`;
}

export function TicketFilterDropdown({
    ticketOptions,
    selectedTicketIds,
    onChange,
    disabled = false,
}: TicketFilterDropdownProps) {
    const isAll = areAllTicketsSelected(ticketOptions, selectedTicketIds);

    const isTicketChecked = (id: number) => isAll || Boolean(selectedTicketIds?.includes(id));

    const toggleAll = (checked: boolean) => {
        onChange(checked ? undefined : []);
    };

    const toggleTicket = (id: number, checked: boolean) => {
        const current = isAll
            ? ticketOptions.map((option) => option.id)
            : (selectedTicketIds ?? []);
        const next = checked
            ? [...current.filter((item) => item !== id), id]
            : current.filter((item) => item !== id);

        if (areAllTicketsSelected(ticketOptions, next)) {
            onChange(undefined);
            return;
        }

        onChange(next);
    };

    return (
        <Popover>
            <PopoverTrigger asChild>
                <button
                    type="button"
                    disabled={disabled}
                    className="reports-ticket-filter-trigger flex h-9 w-full cursor-pointer items-center justify-between whitespace-nowrap rounded-md border border-solid border-border bg-input px-3 py-2 text-left text-sm text-foreground shadow-sm outline-none appearance-none focus:outline-none focus:ring-2 focus:ring-ring disabled:cursor-not-allowed disabled:opacity-50"
                >
                    <span className="min-w-0 truncate">
                        {getTriggerLabel(ticketOptions, selectedTicketIds, isAll)}
                    </span>
                    <ChevronDown className="h-4 w-4 shrink-0 opacity-50" />
                </button>
            </PopoverTrigger>
            <PopoverContent
                align="start"
                className="reports-light-overlay w-[var(--radix-popover-trigger-width)] min-w-[16rem] p-1"
            >
                <div className="max-h-80 overflow-y-auto">
                    <label className="flex cursor-pointer items-center gap-2 rounded-sm px-2 py-1.5 text-sm text-foreground hover:bg-accent">
                        <Checkbox
                            checked={isAll}
                            onCheckedChange={(checked) => toggleAll(checked === true)}
                        />
                        All Tickets
                    </label>
                    {ticketOptions.length > 0 ? <div className="my-1 h-px bg-muted" /> : null}
                    {ticketOptions.map((option) => (
                        <label
                            key={option.id}
                            className="flex cursor-pointer items-center gap-2 rounded-sm px-2 py-1.5 text-sm text-foreground hover:bg-accent"
                        >
                            <Checkbox
                                checked={isTicketChecked(option.id)}
                                onCheckedChange={(checked) =>
                                    toggleTicket(option.id, checked === true)
                                }
                            />
                            <span className="min-w-0 break-words">{option.name}</span>
                        </label>
                    ))}
                </div>
            </PopoverContent>
        </Popover>
    );
}
